// MaterialToolbar — the editor-scoped contextual action cluster, pure.
//
// The middle band is gone (chrome-relocation-and-contextual-toolbar.md): the
// breadcrumb was deleted (the tab title + the top-strip project marker carry
// where-am-I), and the panel summon migrated to the right-dock ribbon. What
// remains is the material EDITOR's own action surface — insert / undo-redo /
// export — a thin, right-aligned cluster that belongs to
// the focused material's document and is present ONLY while a material editor is
// focused. This is Zed's per-buffer `toolbar`, not a resurrected global band:
// it rides inside the editor column, above the document, so the editor keeps
// max canvas and nothing sits between the tab row and the material but this
// slim strip.
//
// Every affordance is optional: an un-insertable / un-exportable material simply
// omits that button. The command palette is NOT a toolbar door — ⌘K lives once,
// in the top strip.
//
// MaterialToolbar owns its chrome strings (Undo/Redo, the issues text) and
// localises them via Lingui. Pure/props-driven: every action reports UP; no
// state, no I/O.

import { useLingui } from "@lingui/react/macro";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import "./MaterialToolbar.css";

export type MaterialToolbarProps = {
    // Open the block palette — the editor's ONE insert surface (master audit
    // R4: the palette belongs to the editor, never to a global "+"). Omitted
    // where the material is not insertable (button hidden).
    readonly onInsert?: () => void;
    readonly onUndo?: () => void;
    readonly onRedo?: () => void;
    readonly canUndo?: boolean;
    readonly canRedo?: boolean;
    // Open the export/packaging flow for this material. Omitted where export is
    // not offered (the button is hidden), present in the authoring app.
    readonly onExport?: () => void;
    readonly className?: string;
};

// A tray-with-down-arrow — "package / export this material". Decorative; the
// IconButton's label carries the meaning.
const exportGlyph = <Icon name="download" />;
const undoGlyph = <Icon name="undo" />;
const redoGlyph = <Icon name="redo" />;
// A plus — "insert a block here". Decorative; the button's text carries it.
// The ONE registry plus form (P33).
const insertGlyph = <Icon name="plus" />;

export const MaterialToolbar = ({
    onInsert,
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
    onExport,
    className,
}: MaterialToolbarProps) => {
    const { t } = useLingui();

    return (
        <div
            className={`bilda-material-toolbar${className ? ` ${className}` : ""}`}
        >
            {/* Insert — the editor's block palette entry (its ONE home). */}
            {onInsert ? (
                <Button variant="ghost" size="md" onClick={onInsert}>
                    <span
                        className="bilda-material-toolbar__insert-glyph"
                        aria-hidden="true"
                    >
                        {insertGlyph}
                    </span>
                    {t`Insert`}
                </Button>
            ) : null}

            {onUndo || onRedo ? (
                <div className="bilda-material-toolbar__history">
                    <IconButton
                        label={t`Undo`}
                        variant="ghost"
                        size="md"
                        disabled={!canUndo}
                        onClick={onUndo}
                    >
                        {undoGlyph}
                    </IconButton>
                    <IconButton
                        label={t`Redo`}
                        variant="ghost"
                        size="md"
                        disabled={!canRedo}
                        onClick={onRedo}
                    >
                        {redoGlyph}
                    </IconButton>
                </div>
            ) : null}

            {onExport ? (
                <IconButton
                    label={t`Export material`}
                    variant="ghost"
                    size="md"
                    onClick={onExport}
                >
                    {exportGlyph}
                </IconButton>
            ) : null}
        </div>
    );
};
